import type { BoatResponse } from '@sea-snag/shared';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, Wand2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { MarketFilters } from '../hooks/use-market-filters';
import type { CompSet } from '../lib/api-client';
import { compSetsApi, marketDataApi } from '../lib/api-client';
import { queryKeys, STALE_TIMES } from '../lib/query-keys';
import { LocationCascadeSingle } from './location-cascade-single';
import { MarketBoatReviewTable } from './market-boat-review-table';
import { ActiveFilterBadges, SearchableMultiSelect } from './market-filter-panel';
import { ReferenceBoatSelector } from './reference-boat-selector';
import { WizardFilterPanel } from './wizard-filter-panel';
import { WizardStepper } from './wizard-stepper';

// ── Types ──

interface CompSetWizardDialogProps {
  mode: 'create' | 'edit';
  orgPid: string;
  initialData?: CompSet;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STEPS = ['Configure', 'Review Boats'];

const NUMERIC_FILTER_KEYS = [
  'minLength',
  'maxLength',
  'minCabins',
  'maxCabins',
  'minBerths',
  'maxBerths',
  'minYear',
  'maxYear',
  'minWc',
  'minPeople',
  'minBeam',
  'maxBeam',
  'minDraught',
  'maxDraught',
] as const;

// ── Component ──

export function CompSetWizardDialog({
  mode,
  orgPid,
  initialData,
  open,
  onOpenChange,
}: CompSetWizardDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Step state ──
  const [currentStep, setCurrentStep] = useState(0);

  // ── Reference boats ──
  const [referenceBoatPids, setReferenceBoatPids] = useState<string[]>([]);
  const [selectedBoats, setSelectedBoats] = useState<BoatResponse[]>([]);

  // ── Location cascade (Country → Sailing Area → Base, issue #95) ──
  // Country is single-select; Sailing Area and Base are multi-select within
  // the chosen country. Match the Market Explorer cascade exactly.
  const [selectedCountryPid, setSelectedCountryPid] = useState<string | undefined>();
  const [selectedSailingAreaPids, setSelectedSailingAreaPids] = useState<string[]>([]);
  const [selectedBasePids, setSelectedBasePids] = useState<string[]>([]);

  // ── Market filters ──
  const [filterKind, setFilterKind] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMinLength, setFilterMinLength] = useState<number | undefined>();
  const [filterMaxLength, setFilterMaxLength] = useState<number | undefined>();
  const [filterMinCabins, setFilterMinCabins] = useState<number | undefined>();
  const [filterMaxCabins, setFilterMaxCabins] = useState<number | undefined>();
  const [filterMinBerths, setFilterMinBerths] = useState<number | undefined>();
  const [filterMaxBerths, setFilterMaxBerths] = useState<number | undefined>();
  const [filterMinYear, setFilterMinYear] = useState<number | undefined>();
  const [filterMaxYear, setFilterMaxYear] = useState<number | undefined>();
  const [filterMinWc, setFilterMinWc] = useState<number | undefined>();
  const [filterMinPeople, setFilterMinPeople] = useState<number | undefined>();
  const [filterMinBeam, setFilterMinBeam] = useState<number | undefined>();
  const [filterMaxBeam, setFilterMaxBeam] = useState<number | undefined>();
  const [filterMinDraught, setFilterMinDraught] = useState<number | undefined>();
  const [filterMaxDraught, setFilterMaxDraught] = useState<number | undefined>();
  const [filterCharterNames, setFilterCharterNames] = useState<string[]>([]);
  const [filterHomeBase, setFilterHomeBase] = useState('');
  const [filterEquipmentIds, setFilterEquipmentIds] = useState<number[]>([]);
  const [filterModels, setFilterModels] = useState<string[]>([]);

  // ── Exclusion ──
  const [excludedPids, setExcludedPids] = useState<string[]>([]);

  // ── Save step ──
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [smartSyncEnabled, setSmartSyncEnabled] = useState(true);

  // ── Data fetching ──

  const { data: locationHierarchy = [] } = useQuery({
    queryKey: queryKeys.marketData.locationHierarchy(orgPid),
    queryFn: () => marketDataApi.locationHierarchy(),
    staleTime: STALE_TIMES.LONG,
    enabled: open,
  });

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.market.filterOptions(orgPid),
    queryFn: () => marketDataApi.filterOptions(),
    staleTime: STALE_TIMES.MEDIUM,
    enabled: open,
  });

  // Models scoped to the selected boat type — same pattern as Market Explorer
  // (issue #104: previously the wizard never fetched these, so the Model
  // dropdown stayed empty even after picking a type).
  const { data: modelOptions } = useQuery({
    queryKey: queryKeys.market.models(orgPid, filterKind),
    queryFn: () => marketDataApi.models(filterKind),
    enabled: open && !!filterKind,
    staleTime: STALE_TIMES.MEDIUM,
  });

  // Sort to keep the cache key stable regardless of selection order.
  const charterCompaniesFilter = useMemo(
    () => ({
      countryPids: selectedCountryPid ? [selectedCountryPid] : undefined,
      sailingAreaPids:
        selectedSailingAreaPids.length > 0 ? [...selectedSailingAreaPids].sort() : undefined,
      basePids: selectedBasePids.length > 0 ? [...selectedBasePids].sort() : undefined,
    }),
    [selectedCountryPid, selectedSailingAreaPids, selectedBasePids]
  );

  const hasLocationFilter =
    selectedCountryPid !== undefined ||
    selectedSailingAreaPids.length > 0 ||
    selectedBasePids.length > 0;

  // Only fetch when a country or base is picked. Without a location filter
  // the endpoint would return the global list, which defeats the
  // "companies with ≥1 boat in your selection" intent.
  const { data: charterCompanyOptions } = useQuery({
    queryKey: queryKeys.marketData.charterCompanies(orgPid, charterCompaniesFilter),
    queryFn: () => marketDataApi.charterCompanies(charterCompaniesFilter),
    staleTime: STALE_TIMES.MEDIUM,
    enabled: open && hasLocationFilter,
  });

  // ── Edit mode pre-population ──

  useEffect(() => {
    if (!initialData || !open) return;
    setName(initialData.name);
    setDescription(initialData.description ?? '');
    setSmartSyncEnabled(initialData.smartSyncEnabled);
    setFilterKind(initialData.filterKind ?? '');
    setFilterMinCabins(initialData.filterMinCabins ?? undefined);
    setFilterMaxCabins(initialData.filterMaxCabins ?? undefined);
    setFilterMinYear(initialData.filterMinYear ?? undefined);
    setFilterMaxYear(initialData.filterMaxYear ?? undefined);
    setFilterMinLength(
      initialData.filterMinLength ? Number(initialData.filterMinLength) : undefined
    );
    setFilterMaxLength(
      initialData.filterMaxLength ? Number(initialData.filterMaxLength) : undefined
    );
    // Legacy comp sets may have multiple country pids (the old multi-select
    // wizard). The new cascade is single-country, so take the first if any
    // — acceptable degradation; user can re-pick on save.
    setSelectedCountryPid(initialData.filterCountryPids?.[0]);
    setSelectedSailingAreaPids(initialData.filterSailingAreaPids ?? []);
    setSelectedBasePids(initialData.filterBasePids ?? []);
    setExcludedPids(initialData.excludedMarketBoatPids ?? []);
    setReferenceBoatPids(initialData.referenceBoats.map((b) => b.id));
    setFilterMinBerths(initialData.filterMinBerths ?? undefined);
    setFilterMaxBerths(initialData.filterMaxBerths ?? undefined);
    setFilterMinWc(initialData.filterMinWc ?? undefined);
    setFilterMinPeople(initialData.filterMinPeople ?? undefined);
    setFilterMinBeam(initialData.filterMinBeam ? Number(initialData.filterMinBeam) : undefined);
    setFilterMaxBeam(initialData.filterMaxBeam ? Number(initialData.filterMaxBeam) : undefined);
    setFilterMinDraught(
      initialData.filterMinDraught ? Number(initialData.filterMinDraught) : undefined
    );
    setFilterMaxDraught(
      initialData.filterMaxDraught ? Number(initialData.filterMaxDraught) : undefined
    );
    setFilterCharterNames(initialData.filterCharterNames ?? []);
    setFilterSearch(initialData.filterSearch ?? '');
    setFilterEquipmentIds(initialData.filterEquipmentIds ?? []);
    setFilterModels(initialData.filterModels ?? []);
  }, [initialData, open]);

  // ── Reset on close ──

  useEffect(() => {
    if (open) return;
    setCurrentStep(0);
    setReferenceBoatPids([]);
    setSelectedBoats([]);
    setSelectedCountryPid(undefined);
    setSelectedSailingAreaPids([]);
    setSelectedBasePids([]);
    setFilterKind('');
    setFilterSearch('');
    setFilterMinLength(undefined);
    setFilterMaxLength(undefined);
    setFilterMinCabins(undefined);
    setFilterMaxCabins(undefined);
    setFilterMinBerths(undefined);
    setFilterMaxBerths(undefined);
    setFilterMinYear(undefined);
    setFilterMaxYear(undefined);
    setFilterMinWc(undefined);
    setFilterMinPeople(undefined);
    setFilterMinBeam(undefined);
    setFilterMaxBeam(undefined);
    setFilterMinDraught(undefined);
    setFilterMaxDraught(undefined);
    setFilterCharterNames([]);
    setFilterHomeBase('');
    setFilterEquipmentIds([]);
    setFilterModels([]);
    setExcludedPids([]);
    setName('');
    setDescription('');
    setSmartSyncEnabled(true);
  }, [open]);

  // ── Filter object for MarketFilterPanel ──

  const filters: MarketFilters = useMemo(
    () => ({
      search: filterSearch,
      kind: filterKind,
      models: filterModels,
      homeBase: filterHomeBase,
      charterNames: filterCharterNames,
      minLength: filterMinLength,
      maxLength: filterMaxLength,
      minCabins: filterMinCabins,
      maxCabins: filterMaxCabins,
      minBerths: filterMinBerths,
      maxBerths: filterMaxBerths,
      minYear: filterMinYear,
      maxYear: filterMaxYear,
      minWc: filterMinWc,
      minPeople: filterMinPeople,
      minBeam: filterMinBeam,
      maxBeam: filterMaxBeam,
      minDraught: filterMinDraught,
      maxDraught: filterMaxDraught,
      equipmentIds: filterEquipmentIds,
      // Wizard manages location state separately via selectedCountryPid /
      // selectedSailingAreaPids / selectedBasePids; these MarketFilters
      // fields are unused here.
      sailingAreaPids: [],
      basePids: [],
    }),
    [
      filterSearch,
      filterKind,
      filterModels,
      filterHomeBase,
      filterCharterNames,
      filterMinLength,
      filterMaxLength,
      filterMinCabins,
      filterMaxCabins,
      filterMinBerths,
      filterMaxBerths,
      filterMinYear,
      filterMaxYear,
      filterMinWc,
      filterMinPeople,
      filterMinBeam,
      filterMaxBeam,
      filterMinDraught,
      filterMaxDraught,
      filterEquipmentIds,
    ]
  );

  const setFilter = useCallback(
    <K extends keyof MarketFilters>(key: K, value: MarketFilters[K]) => {
      switch (key) {
        case 'search':
          setFilterSearch(value as string);
          break;
        case 'kind':
          setFilterKind(value as string);
          break;
        case 'models':
          setFilterModels(value as string[]);
          break;
        case 'homeBase':
          setFilterHomeBase(value as string);
          break;
        case 'charterNames':
          setFilterCharterNames(value as string[]);
          break;
        case 'minLength':
          setFilterMinLength(value as number | undefined);
          break;
        case 'maxLength':
          setFilterMaxLength(value as number | undefined);
          break;
        case 'minCabins':
          setFilterMinCabins(value as number | undefined);
          break;
        case 'maxCabins':
          setFilterMaxCabins(value as number | undefined);
          break;
        case 'minBerths':
          setFilterMinBerths(value as number | undefined);
          break;
        case 'maxBerths':
          setFilterMaxBerths(value as number | undefined);
          break;
        case 'minYear':
          setFilterMinYear(value as number | undefined);
          break;
        case 'maxYear':
          setFilterMaxYear(value as number | undefined);
          break;
        case 'minWc':
          setFilterMinWc(value as number | undefined);
          break;
        case 'minPeople':
          setFilterMinPeople(value as number | undefined);
          break;
        case 'minBeam':
          setFilterMinBeam(value as number | undefined);
          break;
        case 'maxBeam':
          setFilterMaxBeam(value as number | undefined);
          break;
        case 'minDraught':
          setFilterMinDraught(value as number | undefined);
          break;
        case 'maxDraught':
          setFilterMaxDraught(value as number | undefined);
          break;
        case 'equipmentIds':
          setFilterEquipmentIds(value as number[]);
          break;
      }
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilterSearch('');
    setFilterKind('');
    setFilterModels([]);
    setFilterHomeBase('');
    setFilterCharterNames([]);
    setFilterMinLength(undefined);
    setFilterMaxLength(undefined);
    setFilterMinCabins(undefined);
    setFilterMaxCabins(undefined);
    setFilterMinBerths(undefined);
    setFilterMaxBerths(undefined);
    setFilterMinYear(undefined);
    setFilterMaxYear(undefined);
    setFilterMinWc(undefined);
    setFilterMinPeople(undefined);
    setFilterMinBeam(undefined);
    setFilterMaxBeam(undefined);
    setFilterMinDraught(undefined);
    setFilterMaxDraught(undefined);
    setFilterEquipmentIds([]);
  }, []);

  const fillFiltersFromBoats = useCallback(() => {
    if (selectedBoats.length === 0) return;

    const nums = (fn: (b: BoatResponse) => number | null | undefined) =>
      selectedBoats.map(fn).filter((v): v is number => v != null);

    const strs = (fn: (b: BoatResponse) => string | null | undefined) =>
      selectedBoats
        .map(fn)
        .filter((v): v is string => v != null)
        .map(Number)
        .filter((v) => !isNaN(v));

    const minMax = (values: number[]) =>
      values.length > 0 ? { min: Math.min(...values), max: Math.max(...values) } : null;

    const lengths = minMax(strs((b) => b.lengthM));
    const years = minMax(nums((b) => b.year));
    const cabins = minMax(nums((b) => b.cabins));
    const berths = minMax(nums((b) => b.berths));
    const wcs = nums((b) => b.wc);
    const people = nums((b) => b.maxPeople);

    if (lengths) {
      setFilterMinLength(lengths.min);
      setFilterMaxLength(lengths.max);
    }
    // Beam/Draught are no longer exposed in the wizard UI (issue #104),
    // so don't auto-fill them — silent constraints the user can't see
    // would just confuse Step 2's results.
    if (years) {
      setFilterMinYear(years.min);
      setFilterMaxYear(years.max);
    }
    if (cabins) {
      setFilterMinCabins(cabins.min);
      setFilterMaxCabins(cabins.max);
    }
    if (berths) {
      setFilterMinBerths(berths.min);
      setFilterMaxBerths(berths.max);
    }
    if (wcs.length > 0) setFilterMinWc(Math.min(...wcs));
    if (people.length > 0) setFilterMinPeople(Math.min(...people));

    // Set kind if all boats share the same type
    const kinds = [...new Set(selectedBoats.map((b) => b.kind))];
    if (kinds.length === 1) setFilterKind(kinds[0]!);

    // Auto-select the union of models the reference boats are (issue #104).
    const models = [
      ...new Set(
        selectedBoats.map((b) => b.model).filter((m): m is string => m != null && m !== '')
      ),
    ];
    if (models.length > 0) setFilterModels(models);
  }, [selectedBoats]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterSearch) count++;
    if (filterKind) count++;
    if (filterHomeBase) count++;
    if (filterCharterNames.length > 0) count++;
    if (filterModels.length > 0) count++;
    for (const key of NUMERIC_FILTER_KEYS) {
      if (filters[key] !== undefined) count++;
    }
    if (filterEquipmentIds.length > 0) count++;
    return count;
  }, [
    filters,
    filterSearch,
    filterKind,
    filterHomeBase,
    filterCharterNames,
    filterModels,
    filterEquipmentIds,
  ]);

  // ── Browse params for Step 2 ──

  const browseParams = useMemo(() => {
    const params: Record<string, string | number | undefined> = {};
    if (filterKind) params['kind'] = filterKind;
    if (filterSearch) params['search'] = filterSearch;
    if (filterMinLength !== undefined) params['minLength'] = filterMinLength;
    if (filterMaxLength !== undefined) params['maxLength'] = filterMaxLength;
    if (filterMinCabins !== undefined) params['minCabins'] = filterMinCabins;
    if (filterMaxCabins !== undefined) params['maxCabins'] = filterMaxCabins;
    if (filterMinBerths !== undefined) params['minBerths'] = filterMinBerths;
    if (filterMaxBerths !== undefined) params['maxBerths'] = filterMaxBerths;
    if (filterMinYear !== undefined) params['minYear'] = filterMinYear;
    if (filterMaxYear !== undefined) params['maxYear'] = filterMaxYear;
    if (filterMinWc !== undefined) params['minWc'] = filterMinWc;
    if (filterMinPeople !== undefined) params['minPeople'] = filterMinPeople;
    if (filterMinBeam !== undefined) params['minBeam'] = filterMinBeam;
    if (filterMaxBeam !== undefined) params['maxBeam'] = filterMaxBeam;
    if (filterMinDraught !== undefined) params['minDraught'] = filterMinDraught;
    if (filterMaxDraught !== undefined) params['maxDraught'] = filterMaxDraught;
    if (filterCharterNames.length > 0) params['charterNames'] = filterCharterNames.join(',');
    if (filterModels.length > 0) params['models'] = filterModels.join(',');
    if (filterEquipmentIds.length > 0) params['equipmentIds'] = filterEquipmentIds.join(',');

    // Location cascade: the backend now natively understands countryPid +
    // sailingAreaPids in addition to basePids (PR #147). Send whichever
    // levels the user selected — the API resolves them via subqueries.
    if (selectedCountryPid) params['countryPid'] = selectedCountryPid;
    if (selectedSailingAreaPids.length > 0)
      params['sailingAreaPids'] = selectedSailingAreaPids.join(',');
    if (selectedBasePids.length > 0) params['basePids'] = selectedBasePids.join(',');

    return params;
  }, [
    filterKind,
    filterSearch,
    filterMinLength,
    filterMaxLength,
    filterMinCabins,
    filterMaxCabins,
    filterMinBerths,
    filterMaxBerths,
    filterMinYear,
    filterMaxYear,
    filterMinWc,
    filterMinPeople,
    filterMinBeam,
    filterMaxBeam,
    filterMinDraught,
    filterMaxDraught,
    filterCharterNames,
    filterModels,
    filterEquipmentIds,
    selectedCountryPid,
    selectedSailingAreaPids,
    selectedBasePids,
  ]);

  // ── Mutations ──

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof compSetsApi.create>[0]) => compSetsApi.create(data),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.list(orgPid) });
      toast.success(
        smartSyncEnabled
          ? 'Competitor set created — Syncing competitors in the background'
          : 'Competitor set created'
      );
      onOpenChange(false);
      void navigate({ to: '/$orgPid/competitor-sets/$pid', params: { orgPid, pid: result.id } });
    },
    onError: () => toast.error('Failed to create competitor set'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ pid, data }: { pid: string; data: Record<string, unknown> }) =>
      compSetsApi.update(pid, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.detail(initialData!.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.list(orgPid) });
      toast.success('Competitor set updated');
      onOpenChange(false);
    },
    onError: () => toast.error('Failed to update competitor set'),
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // ── Save handler ──

  const handleSave = () => {
    const payload = {
      name,
      description: description || undefined,
      referenceBoatPids: referenceBoatPids.length > 0 ? referenceBoatPids : undefined,
      filterKind: filterKind || undefined,
      filterMinLength: filterMinLength,
      filterMaxLength: filterMaxLength,
      filterMinCabins: filterMinCabins,
      filterMaxCabins: filterMaxCabins,
      filterMinYear: filterMinYear,
      filterMaxYear: filterMaxYear,
      filterMinBerths: filterMinBerths,
      filterMaxBerths: filterMaxBerths,
      filterMinWc: filterMinWc,
      filterMinPeople: filterMinPeople,
      filterMinBeam: filterMinBeam,
      filterMaxBeam: filterMaxBeam,
      filterMinDraught: filterMinDraught,
      filterMaxDraught: filterMaxDraught,
      // Country is single-select but the persisted shape stays an array
      // for backward compatibility with legacy data + the existing API.
      filterCountryPids: selectedCountryPid ? [selectedCountryPid] : undefined,
      filterSailingAreaPids:
        selectedSailingAreaPids.length > 0 ? selectedSailingAreaPids : undefined,
      filterBasePids: selectedBasePids.length > 0 ? selectedBasePids : undefined,
      filterCharterNames: filterCharterNames.length > 0 ? filterCharterNames : undefined,
      filterModels: filterModels.length > 0 ? filterModels : undefined,
      filterSearch: filterSearch.trim() ? filterSearch.trim() : undefined,
      filterEquipmentIds: filterEquipmentIds.length > 0 ? filterEquipmentIds : undefined,
      excludedMarketBoatPids: excludedPids.length > 0 ? excludedPids : undefined,
      smartSyncEnabled,
    };

    if (mode === 'edit' && initialData) {
      updateMutation.mutate({ pid: initialData.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // ── Step validation ──

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 0:
        return selectedCountryPid !== undefined && name.trim().length > 0;
      case 1:
        return true;
      default:
        return false;
    }
  }, [currentStep, selectedCountryPid, name]);

  // ── Completed steps for stepper ──

  const completedSteps = useMemo(() => {
    const completed = new Set<number>();
    if (currentStep > 0) completed.add(0);
    return completed;
  }, [currentStep]);

  // ── Summary helpers for Step 3 ──

  // ── Render ──

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[90vw] w-[1400px] h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Competitor Set' : 'Edit Competitor Set'}
          </DialogTitle>
          <DialogDescription>
            {currentStep === 0 &&
              'Name your set, select reference boats, and define filters to find competitors.'}
            {currentStep === 1 && 'Preview boats matching your filters. Uncheck any to exclude.'}
          </DialogDescription>
        </DialogHeader>

        {/* Main layout */}
        <div className="flex gap-6 flex-1 overflow-hidden">
          {/* Sidebar: Stepper + Active Filters */}
          <div className="flex flex-col w-56 shrink-0 overflow-hidden">
            <WizardStepper
              steps={STEPS}
              currentStep={currentStep}
              onStepClick={setCurrentStep}
              completedSteps={completedSteps}
            />

            {(selectedBoats.length > 0 || activeFilterCount > 0) && (
              <div className="mt-4 pt-4 border-t overflow-y-auto flex-1 space-y-4">
                {selectedBoats.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Reference Boats
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedBoats.map((boat) => (
                        <Badge key={boat.id} variant="secondary" className="gap-1 pr-1 text-xs">
                          <span className="max-w-[140px] truncate">{boat.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setReferenceBoatPids(referenceBoatPids.filter((p) => p !== boat.id));
                              setSelectedBoats(selectedBoats.filter((b) => b.id !== boat.id));
                            }}
                            className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
                            aria-label={`Remove ${boat.name}`}
                          >
                            <X className="size-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {activeFilterCount > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Active Filters
                    </h4>
                    <ActiveFilterBadges
                      filters={filters}
                      setFilter={setFilter}
                      resetFilters={resetFilters}
                      filterOptions={filterOptions}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto scrollbar-none space-y-6 pr-2">
            {/* Step 0: Configure */}
            {currentStep === 0 && (
              <div className="space-y-6">
                {/* Name & Description */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cs-name">Name *</Label>
                    <Input
                      id="cs-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Croatia Sailing Yachts 40-50ft"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cs-desc">Description</Label>
                    <Input
                      id="cs-desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description..."
                    />
                  </div>
                </div>

                {/* Reference Boats */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Reference Boats</h3>
                  <p className="text-xs text-muted-foreground">
                    Choose your own boats to compare against the market (optional).
                  </p>
                  <ReferenceBoatSelector
                    orgPid={orgPid}
                    selectedPids={referenceBoatPids}
                    onChange={setReferenceBoatPids}
                    onBoatsChange={setSelectedBoats}
                    hideBadges
                  />
                  {selectedBoats.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs bg-violet-100 hover:bg-violet-200 border-violet-200 text-violet-700 dark:bg-violet-950 dark:hover:bg-violet-900 dark:border-violet-800 dark:text-violet-300"
                      onClick={fillFiltersFromBoats}
                    >
                      <Wand2 className="size-3.5" />
                      Match selected
                    </Button>
                  )}
                </div>

                {/* Location */}
                <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Location
                  </h4>
                  <LocationCascadeSingle
                    countries={locationHierarchy}
                    countryPid={selectedCountryPid}
                    sailingAreaPids={selectedSailingAreaPids}
                    basePids={selectedBasePids}
                    onCountryChange={setSelectedCountryPid}
                    onSailingAreaPidsChange={setSelectedSailingAreaPids}
                    onBasePidsChange={setSelectedBasePids}
                  />
                  <SearchableMultiSelect
                    label="Charter Company"
                    selected={filterCharterNames}
                    onChange={setFilterCharterNames}
                    options={charterCompanyOptions ?? filterOptions?.charterNames ?? []}
                    placeholder="Any company"
                  />
                </div>

                {/* Filters */}
                <WizardFilterPanel
                  filters={filters}
                  setFilter={setFilter}
                  filterOptions={filterOptions}
                  modelOptions={modelOptions}
                />

                {/* Smart Sync */}
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="cs-sync">Smart Sync</Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically add new boats that match your filters
                    </p>
                  </div>
                  <Switch
                    id="cs-sync"
                    checked={smartSyncEnabled}
                    onCheckedChange={setSmartSyncEnabled}
                  />
                </div>
              </div>
            )}

            {/* Step 1: Review Boats */}
            {currentStep === 1 && (
              <MarketBoatReviewTable
                orgPid={orgPid}
                browseParams={browseParams}
                excludedPids={excludedPids}
                onExcludedChange={setExcludedPids}
                enabled={selectedCountryPid !== undefined}
              />
            )}
          </div>
        </div>

        {/* Navigation footer */}
        <div className="flex justify-between pt-4 border-t">
          {currentStep > 0 ? (
            <Button variant="outline" onClick={() => setCurrentStep((s) => s - 1)}>
              Back
            </Button>
          ) : (
            <div />
          )}
          {currentStep < 1 ? (
            <Button onClick={() => setCurrentStep((s) => s + 1)} disabled={!canProceed}>
              Next
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={!name.trim() || saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
